import { useForm, Link, router } from "@inertiajs/react";
import type Category from "@/types/serializers/Category";
import { useFlashToast } from "@/hooks/useFlashToast";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { ArrowLeft, Trash2 } from "lucide-react";

type Props = {
  category: Category;
  errors?: Record<string, string[]>;
  flash?: { alert?: string };
};

export default function CategoriesEdit({ category, errors = {}, flash }: Props) {
  useFlashToast(flash);

  const form = useForm({
    category: {
      name: category.name ?? "",
      description: category.description ?? "",
    },
  });

  const c = form.data.category;

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    form.put(`/categories/${category.id}`);
  }

  function handleDelete() {
    if (!confirm("Are you sure you want to delete this category?")) return;
    router.delete(`/categories/${category.id}`);
  }

  return (
    <div className="py-8 px-4">
      <div className="mx-auto max-w-xl space-y-6">
        <Button variant="ghost" size="sm" asChild>
          <Link href="/categories">
            <ArrowLeft className="size-4" />
            Back to categories
          </Link>
        </Button>

        <Card>
          <CardHeader>
            <CardTitle>Edit Category</CardTitle>
            <CardDescription>
              Update the details for this category.
            </CardDescription>
          </CardHeader>
          <form onSubmit={handleSubmit}>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="name">Name</Label>
                <Input
                  id="name"
                  type="text"
                  placeholder="e.g. Whiskey, Vodka, Tequila…"
                  value={c.name}
                  onChange={(e) =>
                    form.setData("category", { ...c, name: e.target.value })
                  }
                  aria-invalid={!!errors.name}
                  autoFocus
                  required
                />
                {errors.name?.[0] && (
                  <p className="text-sm text-destructive">{errors.name[0]}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  placeholder="Optional description for this category…"
                  value={c.description}
                  onChange={(e) =>
                    form.setData("category", { ...c, description: e.target.value })
                  }
                  rows={4}
                  aria-invalid={!!errors.description}
                />
                {errors.description?.[0] && (
                  <p className="text-sm text-destructive">{errors.description[0]}</p>
                )}
              </div>
            </CardContent>
            <CardFooter className="flex justify-between">
              <div className="flex gap-2">
                <Button variant="outline" asChild>
                  <Link href="/categories">Cancel</Link>
                </Button>
                <Button
                  type="button"
                  variant="destructive"
                  onClick={handleDelete}
                >
                  <Trash2 className="size-4" />
                  Delete
                </Button>
              </div>
              <Button type="submit" disabled={form.processing}>
                {form.processing ? "Saving…" : "Save Changes"}
              </Button>
            </CardFooter>
          </form>
        </Card>
      </div>
    </div>
  );
}
